/* Walk-set PDP layout.
 *
 * The personalisation flow is spread across two independent theme blocks (the bundle
 * builder and the charm builder), so the natural DOM order is builder-cards first,
 * personalisation last. The order the customer actually needs is:
 *
 *   1. collar/leash colour   2. dog's name   3. the rendered strap
 *   4. charm colour          5. size (+ size guide)
 *   6. the chosen collar and leash, small, purely to confirm the variant
 *
 * That interleaves the two blocks, so it can't be done by reordering blocks in the
 * theme editor — the nodes are relocated here instead. mpb.js builds its cards once
 * (renderAll is not re-run on selection), and it keeps live references to the label
 * elements, so moving them is safe and they keep updating themselves.
 */
(function () {
  "use strict";

  const READY_ATTR = "data-wsl-ready";

  function build(root) {
    if (root.getAttribute(READY_ATTR) === "1") return true;
    /* build() MOVES nodes out of .cni. If a previous pass already did that and its
     * wrapper is still on the page, a second pass finds a rebuilt .cni and lays its rows
     * out again — two name fields, two straps, two charm-colour rows. One wrapper only. */
    const existing = document.querySelector(".wsl");
    if (existing && existing.isConnected) return true;

    const list   = root.querySelector('[id^="mpb-list-"]');
    const shared = root.querySelector(".mpb-shared-color");
    const cni    = document.querySelector(".cni");
    if (!list || !shared || !cni) return false;

    const cniRows   = Array.from(cni.querySelectorAll(".cni__row"));
    const nameRow   = cniRows.find((r) => r.querySelector(".cni__input"));
    const colorRow  = cniRows.find((r) => r.querySelector(".cni__colors"));
    const strap     = cni.querySelector(".cni__strap");
    const symbols   = cni.querySelector(".cni__symbols");
    const cards     = Array.from(list.querySelectorAll(".mpb-card"));
    if (!nameRow || !colorRow || !strap || !cards.length) return false;

    root.setAttribute(READY_ATTR, "1");

    const wrap = document.createElement("div");
    wrap.className = "wsl";

    /* Give the main colour row a heading too, but only when mpb.js flagged that a
     * second group exists — otherwise it's the only colour choice and needs no name. */
    let sharedNode = shared;
    if (shared.dataset.groupLabel) {
      const sec = document.createElement("div");
      sec.className = "wsl__slot-color wsl__slot-color--main";
      const h = document.createElement("div");
      h.className = "wsl__slot-color-title";
      h.textContent = shared.dataset.groupLabel;
      sec.append(h, shared);
      sharedNode = sec;
    }

    // 1-4: colour, name, symbol buttons, strap, charm colours
    wrap.append(sharedNode, nameRow);
    if (symbols) wrap.appendChild(symbols);
    wrap.append(strap, colorRow);

    /* Second colour group (items sold as their own matched pair on a smaller palette)
     * sits directly under the main colour row as its own labelled section. */
    const altColor = root.querySelector(".mpb-shared-color--alt");
    if (altColor) {
      const section = document.createElement("div");
      section.className = "wsl__slot-color";
      const heading = document.createElement("div");
      heading.className = "wsl__slot-color-title";
      heading.textContent = altColor.dataset.groupLabel || "Colour";
      section.append(heading, altColor);
      sharedNode.parentNode.insertBefore(section, sharedNode.nextSibling);
    }

    // 5: size controls, lifted out of the cards (this carries the size-guide link)
    const sizes = document.createElement("div");
    sizes.className = "wsl__sizes";
    cards.forEach((card) => {
      const size = card.querySelector(".mpb-opt--size");
      if (size) sizes.appendChild(size);
    });
    if (sizes.children.length) wrap.appendChild(sizes);

    // 6: the chosen items, image + live variant label only
    const items = document.createElement("div");
    items.className = "wsl__items";
    cards.forEach((card) => {
      const img = card.querySelector(".mpb-card__img");
      if (!img) return;
      const cell = document.createElement("div");
      cell.className = "wsl__item";
      cell.appendChild(img);

      /* The colour label is mpb.js's own element — it keeps updating after the move. */
      const label = card.querySelector(".mpb-opt--color .mpb-opt__label");
      if (label) { label.classList.add("wsl__item-label"); cell.appendChild(label); }

      /* FREE badge travels with the thumbnail. The preorder note does NOT — it's
       * collected into its own row below, so it still shows when the thumbnails are
       * turned off (which is the default). */
      const freeTag = card.querySelector(".mpb-free-tag");
      if (freeTag) cell.insertBefore(freeTag, cell.firstChild);

      items.appendChild(cell);
      card.classList.add("is-compacted");
    });
    /* The thumbnails are behind a block setting so they can be brought back without
     * a code change. Build them either way — only the display is gated. */
    const showItems = String(root.dataset.showItemPreviews || "") === "true";
    if (items.children.length && showItems) wrap.appendChild(items);

    /* Preorder notices, lifted out of the (hidden) cards so a backordered size still
     * tells the customer before they buy. mpb.js keeps updating these nodes, so
     * moving them preserves the live show/hide per item. */
    const notices = document.createElement("div");
    notices.className = "wsl__preorder";
    cards.forEach((card) => {
      const note = card.querySelector(".mpb-preorder");
      if (note) notices.appendChild(note);
    });
    if (notices.children.length) wrap.appendChild(notices);

    list.parentNode.insertBefore(wrap, list);

    /* Chrome the walk-set layout makes redundant. Hidden inline (a class was losing to
     * the theme's own rules) and tagged, so `.wsl-hidden` elements can be revealed
     * again by clearing the inline style. */
    const hide = (el) => {
      if (!el) return;
      el.classList.add("wsl-hidden");
      el.style.display = "none";
    };

    /* Duplicate of the bundle headline, which is the price that reflects the set. */
    const themePrice = document.querySelector(".price__container");
    if (themePrice && !root.contains(themePrice)) hide(themePrice);

    /* The headline already states the price; the Total row repeats it. */
    hide(root.querySelector(".mpb__total"));

    /* The value anchor already tells the saving story — the % pill duplicates it. */
    hide(root.querySelector(".mpb__headline-badge"));
    document.querySelectorAll(".popular_bade").forEach(hide);

    /* The reassurance row covers what the accordions said, so they're redundant here. */
    document.querySelectorAll(".product-block-collapsible-row").forEach(hide);

    buildTrustRow();
    moveRatingToTop(root);
    buildBenefits(root);
    return true;
  }

  /* Checkmark benefits, directly under the price block. Configured as one per line so
   * they can be edited per set without touching code. */
  function buildBenefits(root) {
    if (root.querySelector(".wsl__benefits")) return;
    const raw = root.dataset.benefits || "";
    const items = raw.split(/[\r\n]+/).map((x) => x.trim()).filter(Boolean);
    if (!items.length) return;

    const headline = root.querySelector(".mpb__headline");
    if (!headline || !headline.parentNode) return;

    const ul = document.createElement("ul");
    ul.className = "wsl__benefits";
    ul.innerHTML = items.map((text) => `
      <li class="wsl__benefit">
        <svg class="wsl__benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 12.5l5 5L20 6.5"/>
        </svg>
        <span>${text}</span>
      </li>`).join("");

    /* After the rating if it's there, otherwise straight after the price. */
    const anchor = root.querySelector(".wsl__rating") || headline;
    anchor.parentNode.insertBefore(ul, anchor.nextSibling);
  }

  /* Star rating belongs beside the price, not further down the column. Moved rather
   * than duplicated so it stays the theme's own widget and populates itself. */
  function moveRatingToTop(root) {
    const rating = document.querySelector(".product-block--product-rating");
    const headline = root.querySelector(".mpb__headline");
    if (!rating || !headline || !headline.parentNode) return;
    if (rating.dataset.wslMoved === "1") return;
    rating.dataset.wslMoved = "1";
    rating.classList.add("wsl__rating");
    headline.parentNode.insertBefore(rating, headline.nextSibling);

    /* These set products are new and have no reviews of their own, so the theme's
     * rating block renders nothing. The reviews section further down the page is
     * already pointed at an established product — read ITS id rather than hard-coding
     * one, so the stars at the top always agree with the reviews below. */
    if (rating.querySelector(".junip-product-rating, .junip-product-review")) return;
    const src = document.querySelector(".junip-product-review[data-product-id]");
    const pid = src && src.getAttribute("data-product-id");
    if (!pid) return;
    const span = document.createElement("span");
    span.className = "junip-product-rating";
    span.setAttribute("data-product-id", pid);
    rating.appendChild(span);
    /* Junip scans on load; nudge it to pick up a node added after that. */
    try { window.junip && window.junip.reload && window.junip.reload(); } catch (e) {}
  }

  /* Reassurance icons under Add to Cart. Rendered here rather than as another theme
   * block so the walk-set layout owns its whole column. */
  function buildTrustRow() {
    if (document.querySelector(".wsl__trust")) return;
    const anchor = document.querySelector(".product__policies") ||
                   document.querySelector(".pdp-delivery-time");
    if (!anchor || !anchor.parentNode) return;

    const ICONS = {
      guarantee: '<path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/>',
      shipping:  '<path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17" cy="17" r="1.6"/>',
      rated:     '<path d="M12 4l2.3 4.7 5.2.8-3.8 3.6.9 5.1-4.6-2.4-4.6 2.4.9-5.1L4.5 9.5l5.2-.8z"/>'
    };
    const ITEMS = [
      { k: "guarantee", label: "30-Day Satisfaction Guarantee" },
      { k: "shipping",  label: "Fast Free Shipping" },
      { k: "rated",     label: "Top Rated by Dog Parents" }
    ];

    const row = document.createElement("div");
    row.className = "wsl__trust";
    row.innerHTML = ITEMS.map((it) => `
      <div class="wsl__trust-item">
        <svg class="wsl__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">${ICONS[it.k]}</svg>
        <span class="wsl__trust-label">${it.label}</span>
        ${it.k === "rated"
          ? '<span class="wsl__trust-stars" aria-label="5 out of 5 stars">\u2605\u2605\u2605\u2605\u2605</span>'
          : ''}
      </div>`).join("");

    anchor.parentNode.insertBefore(row, anchor.nextSibling);
  }

  function boot() {
    document.querySelectorAll('.mpb[data-name-mode], .mpb').forEach((root) => {
      if (!document.querySelector('[data-name-mode="1"]')) return;
      if (build(root)) return;
      /* Both blocks render asynchronously; retry briefly until both are present. */
      let tries = 0;
      const t = setInterval(() => {
        if (build(root) || ++tries > 40) clearInterval(t);
      }, 150);
    });
  }

  /* ---- parent colour drives the bundle ----
   *
   * The walk-set parent used to be a single Default Title variant; core-walk-set was given
   * its own Color option in Sep 2026, and the theme's variant selector drives price and the
   * hero image off it. Nothing told the bundle underneath, so changing colour at the top
   * left the collar and leash on the previous one. mpb.js exposes setColor for exactly this.
   *
   * Read the SELECTED SWATCH, not :checked. Every variant radio on the page shares
   * name="id" and submits a whole variant id, so only one radio is ever checked and after a
   * size click that one is a size — asking "which colour is checked?" gets the wrong answer.
   * .swatch--selected is the per-group marker the theme itself maintains.
   *
   * One direction only. MPB never writes the parent's variant id, so there is no loop to
   * guard against; and a colour the parent does not carry (Sky/Green and the Limited
   * Edition trio have no parent variant) simply never fires this, which leaves the bundle
   * row free to select it on its own. */
  function parentColour() {
    const groups = document.querySelectorAll("[js-variant-group-container]");
    for (let i = 0; i < groups.length; i++) {
      const labelEl = groups[i].querySelector(".label-text");
      if (!labelEl || !/colou?r/i.test(labelEl.textContent || "")) continue;
      const sel = groups[i].querySelector(".swatch--selected");
      if (sel) return (sel.getAttribute("data-value") || "").trim();
    }
    return "";
  }

  let lastParentColour = "";
  function syncBundleColour() {
    if (__echo) return;
    const val = parentColour();
    if (!val || val === lastParentColour) return;
    if (window.MPB && typeof window.MPB.setColor === "function") {
      if (window.MPB.setColor(val)) lastParentColour = val;
    }
  }

  /* One colour picker, not two.
   *
   * core-walk-set was given its own Color option, so the theme's variant selector and the
   * bundle's colour row both render — six colours in one, ten plus Limited Edition in the
   * other. The bundle's row is the functional one: it drives the collar and leash that are
   * actually sold. The parent's variants carry no SKU, all cost the same and exist only to
   * hold a hero image, so the native row is worth hiding rather than extending — the four
   * colours it lacks have no set photography to point at anyway.
   *
   * Hide it and drive it instead: clicking the matching native swatch is what updates the
   * price, the gallery and the variant that goes to cart, and the theme already does all
   * of that off its own click handler. Colours the parent does not carry simply leave the
   * hero where it is, which is what they do today. */
  let __echo = false;
  function bindParentPicker() {
    const groups = Array.prototype.slice.call(document.querySelectorAll("[js-variant-group-container]"));
    const group = groups.filter(function (g) {
      const l = g.querySelector(".label-text");
      return l && /colou?r/i.test(l.textContent || "");
    })[0];
    if (!group) return;                        // no parent colour option: nothing to hide
    if (!document.querySelector(".mpb[data-bid]")) return;   // no bundle: leave it alone
    group.style.display = "none";
    document.addEventListener("click", function (e) {
      const sw = e.target.closest && e.target.closest(".mpb-swatch");
      if (!sw) return;
      setTimeout(function () {
        const want = (sw.getAttribute("aria-label") || "").trim().toLowerCase();
        const btns = Array.prototype.slice.call(group.querySelectorAll(".swatch[data-value]"));
        const hit = btns.filter(function (b) {
          return (b.getAttribute("data-value") || "").trim().toLowerCase() === want;
        })[0];
        if (!hit || hit.classList.contains("swatch--selected")) return;
        /* Suppress the echo: this click fires product:variant:changed, which drives
         * syncBundleColour straight back into the bundle we just read from. */
        __echo = true;
        hit.click();
        setTimeout(function () { __echo = false; }, 200);
      }, 30);
    });
  }

  function watchParentColour() {
    /* Only on a real variant change. An earlier version also polled on load until the
     * bundle accepted a colour, which drove a rebuild while build() had already moved the
     * charm builder's rows out of .cni — leaving the old moved copy and a freshly built
     * one on the page at once. Nothing needs syncing at load: the bundle and the parent
     * both start on their own defaults, and the customer has not chosen anything yet.
     *
     * The selector is replaced on every variant change, so listen on document. */
    document.addEventListener("product:variant:changed", () => setTimeout(syncBundleColour, 40));
  }

  ["DOMContentLoaded", "shopify:section:load"].forEach((e) => document.addEventListener(e, boot));
  if (document.readyState !== "loading") boot();
  watchParentColour();
  ["DOMContentLoaded", "shopify:section:load"].forEach(function (e) {
    document.addEventListener(e, bindParentPicker);
  });
  if (document.readyState !== "loading") bindParentPicker();
})();
